import { parseOptionalFiniteNumber } from '../utils/optionalNumber';
import { apiGet } from './apiClient';

export interface MarketDataRecord {
  id: number;
  sector: string;
  company_id: string;
  company_name?: string;
  aum?: number | null;
  employee_amount?: number | null;
  /** @deprecated Формальная должность в компании; для группировки используйте family_name + grade_level */
  position: string;
  family_id?: number | null;
  family_name?: string | null;
  grade_level?: number | null;
  role_name?: string | null;
  fix: number;
  sti: number;
  ltip: string;
  /** Форма LTIP; null если LTIP нет или указано «Нет» */
  ltip_form?: string | null;
  /** Годовой эквивалент цели LTI, ₽. null — сумма не указана. */
  target_lti?: number | null;
  /** Годовой эквивалент фактической выплаты LTI, ₽. null — сумма не указана. */
  ltip_amount?: number | null;
  source: string;
  reliability_score: number;
  relevance_date: string;
}

export interface PackageAccessInfo {
  hasAccess: boolean;
  packageName: string;
}

function normalizeMarketRecord(raw: Record<string, unknown>): MarketDataRecord {
  const employeeRaw =
    raw.employee_amount !== undefined ? raw.employee_amount : (raw as { employeeAmount?: unknown }).employeeAmount;
  const aumRaw = raw.aum !== undefined ? raw.aum : (raw as { Aum?: unknown }).Aum;
  const ltipFormRaw =
    raw.ltip_form !== undefined
      ? raw.ltip_form
      : (raw as { ltipForm?: unknown }).ltipForm;
  const ltipForm =
    ltipFormRaw != null && String(ltipFormRaw).trim() !== '' ? String(ltipFormRaw).trim() : null;

  return {
    id: Number(raw.id),
    sector: String(raw.sector ?? ''),
    company_id: String(raw.company_id ?? ''),
    company_name: raw.company_name != null ? String(raw.company_name) : undefined,
    aum: parseOptionalFiniteNumber(aumRaw),
    employee_amount: parseOptionalFiniteNumber(employeeRaw),
    position: String(raw.position ?? ''),
    family_id: raw.family_id != null ? Number(raw.family_id) : null,
    family_name: raw.family_name != null ? String(raw.family_name) : null,
    grade_level: parseOptionalFiniteNumber(raw.grade_level),
    role_name: raw.role_name != null ? String(raw.role_name) : null,
    fix: parseOptionalFiniteNumber(raw.fix) ?? NaN,
    sti: parseOptionalFiniteNumber(raw.sti) ?? NaN,
    ltip: String(raw.ltip ?? ''),
    ltip_form: ltipForm,
    target_lti: parseOptionalFiniteNumber(raw.target_lti),
    ltip_amount: parseOptionalFiniteNumber(raw.ltip_amount),
    source: String(raw.source ?? ''),
    reliability_score: Number(raw.reliability_score),
    relevance_date: String(raw.relevance_date ?? ''),
  };
}

export const getMarketDataByTableName = async (tableName: string): Promise<MarketDataRecord[]> => {
  try {
    const payload = await apiGet<{ data?: unknown[] }>(
      `/market/records?packageName=${encodeURIComponent(tableName)}`,
    );
    if (!payload) {
      console.error(`Error fetching data from backend for package ${tableName}`);
      return [];
    }
    const data = Array.isArray(payload?.data) ? payload.data : [];

    return (data as Record<string, unknown>[]).map((row) => normalizeMarketRecord(row));
  } catch (error) {
    console.error(`Error in getMarketDataByTableName for package ${tableName}:`, error);
    return [];
  }
};

// Функция для проверки доступа пользователя к пакету
export const checkPackageAccess = async (packageName: string): Promise<boolean> => {
  try {
    const payload = await apiGet<{ hasAccess?: boolean }>(
      `/packages/${encodeURIComponent(packageName)}/access`,
    );
    if (!payload) {
      console.error('Error checking package access');
      return false;
    }
    return Boolean(payload?.hasAccess);
  } catch (error) {
    console.error('Error in checkPackageAccess:', error);
    return false;
  }
};

// Функция для получения информации о доступе к пакету
export const getPackageAccessInfo = async (packageName: string): Promise<PackageAccessInfo> => {
  const hasAccess = await checkPackageAccess(packageName);
  return {
    hasAccess,
    packageName
  };
};

export const getMarketDataByPackageName = async (packageName: string): Promise<MarketDataRecord[]> => {
  if (!packageName || packageName.trim() === '') {
    console.error('Package name is empty or null');
    return [];
  }

  // Проверяем доступ перед загрузкой данных
  const hasAccess = await checkPackageAccess(packageName);
  if (!hasAccess) {
    console.warn(`Access denied for package: ${packageName}`);
    return [];
  }

  return getMarketDataByTableName(packageName);
};
